import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import GalleryModal from '../components/GalleryModal'
import GoogleMap from '../components/GoogleMap'
import CarCard from '../components/CarCard'
import { getResizedImageUrl } from '../lib/utils'

export default function CarDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [car, setCar] = useState(null)
  const [relatedCars, setRelatedCars] = useState([])
  const [loading, setLoading] = useState(true)

  // Valgt billede i hero galleri
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  // Lightbox fuldskærmstilstand
  const [activeImageIndex, setActiveImageIndex] = useState(-1)

  // Henvendelsesformular tilstand
  const [formPurpose, setFormPurpose] = useState('proevetur')
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [formError, setFormError] = useState('')
  const [formLoading, setFormLoading] = useState(false)

  // Flydende Bestil prøvetur CTA-bjælke
  const [showStickyBar, setShowStickyBar] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const contactEl = document.getElementById('kontakt-sektion')

      // Vises når brugeren er scrollet ned forbi hero-knappen
      const isPastHero = scrollY > 450

      // Skjules når kontaktformularen er aktivt i syne
      let isNearContact = false
      if (contactEl) {
        const rect = contactEl.getBoundingClientRect()
        if (rect.top < window.innerHeight * 0.9 && rect.bottom > 80) {
          isNearContact = true
        }
      }

      setShowStickyBar(isPastHero && !isNearContact)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setLoading(true)
    setSelectedImageIndex(0)

    // Hent bil via slug fra Bilinfo-cache endpoint med fallback til CMS lagerbiler
    fetch(`/_fn/cars?slug=${encodeURIComponent(slug)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Ikke i Bilinfo cachen')
        return res.json()
      })
      .then((data) => {
        const vehicle = data.vehicle || data
        if (!vehicle) throw new Error('Bil ikke fundet')
        return vehicle
      })
      .catch(() => {
        return fetch('/_api/public/content/lagerbiler')
          .then((res) => res.json())
          .then((cmsList) => {
            const list = Array.isArray(cmsList) ? cmsList : (cmsList?.items || [])
            const found = list.find(
              (c) => c.slug === slug || c.slug?.da === slug || c.id === slug
            )
            if (!found) throw new Error('Bil ikke fundet')
            return found
          })
      })
      .then((vehicle) => {
        setCar(vehicle)

        // Hent alle biler for at finde relaterede biler på lager
        return fetch('/_fn/cars')
          .then((res) => res.json())
          .then((allData) => {
            const allCars = allData.vehicles || (Array.isArray(allData) ? allData : [])
            if (Array.isArray(allCars)) {
              const currentId = vehicle.Id || vehicle.VehicleId || vehicle.id
              const filtered = allCars
                .filter(
                  (c) =>
                    c.slug !== slug &&
                    c.slug?.da !== slug &&
                    c.Id !== currentId &&
                    c.VehicleId !== currentId
                )
                .slice(0, 3)
              setRelatedCars(filtered)
            }
            setLoading(false)
          })
      })
      .catch((err) => {
        console.error(err)
        setLoading(false)
        navigate('/404', { replace: true })
      })
  }, [slug, navigate])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6]">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-[#9b784a] border-t-transparent" />
      </div>
    )
  }

  if (!car) return null

  const title =
    car.title ||
    car.titel?.da ||
    car.titel ||
    `${car.Make || ''} ${car.Model || ''} ${car.Variant || ''}`.trim()
  const fuel = car.fuel || car.Propellant || car.braendstof?.da || car.braendstof || 'Ukendt'
  const transmission =
    car.transmission ||
    (car.GearType === 'A' ? 'Automatgear' : car.GearType === 'M' ? 'Manuel' : car.GearType) ||
    car.gear?.da ||
    car.gear ||
    'Automatgear'
  const descriptionHtml = car.Comment
    ? `<p>${car.Comment.replace(/\n\n+/g, '</p><p>').replace(/\n/g, '<br/>')}</p>`
    : car.beskrivelse?.da || car.beskrivelse || '<p>Ingen beskrivelse tilgængelig.</p>'
  const images =
    car.images && car.images.length > 0
      ? car.images
      : car.Pictures && car.Pictures.length > 0
      ? car.Pictures
      : car.galleri || []
  const isSold = car.solgt_status === true
  const isLeasingFlag = Boolean(
    car.er_leasing === true ||
    car.er_leasing === 'true' ||
    car.isLeasing === true ||
    car.is_leasing === true ||
    car.PriceType === 'LeasingPrice' ||
    car.SaleType === 'Leasing' ||
    car.Leasing === true ||
    car.Leasing === 'true'
  )
  const year = car.year || parseInt(car.Year || car.aargang || 0, 10)
  const mileage = car.mileage ?? parseInt(car.Mileage || car.kilometer || 0, 10)

  // Priser: Både kontantpris og leasingpris understøttes
  const rawCash = parseInt(car.kontantpris || car.CashPrice || 0, 10)
  const rawLeasing = parseInt(car.leasingpris || car.LeasingPrice || car.LeasingMonthlyPayment || 0, 10)
  const fallbackPrice = parseInt(car.pris || car.price || car.Price || 0, 10)

  let cashPrice = 0
  let leasingPrice = 0

  if (rawCash > 0 && rawLeasing > 0) {
    cashPrice = rawCash
    leasingPrice = rawLeasing
  } else if (rawLeasing > 0) {
    leasingPrice = rawLeasing
    cashPrice = rawCash > 0 ? rawCash : (fallbackPrice > 0 && fallbackPrice !== rawLeasing ? fallbackPrice : 0)
  } else if (rawCash > 0) {
    cashPrice = rawCash
  } else if (fallbackPrice > 0) {
    if (isLeasingFlag) {
      leasingPrice = fallbackPrice
    } else {
      cashPrice = fallbackPrice
    }
  }

  const hasBoth = cashPrice > 0 && leasingPrice > 0
  const isLeasing = leasingPrice > 0 && cashPrice === 0
  const price = cashPrice || leasingPrice || fallbackPrice
  const range = car.Range || car.range || car.raekkevidde
  const horsepower = car.Effect || car.hestekraefter
  const battery = car.BatterySize || car.batteri_kapacitet
  const consumption = car.ElectricConsumption
    ? `${car.ElectricConsumption} Wh/km`
    : car.KmPerLiter
    ? `${car.KmPerLiter} km/l`
    : car.forbrug?.da || car.forbrug
  const greenTax = car.GreenTax
    ? `${parseInt(car.GreenTax, 10).toLocaleString('da-DK')} kr. / ${
        car.GreenTaxPeriod === '12' ? 'år' : 'halvår'
      }`
    : car.groen_ejerafgift?.da || car.groen_ejerafgift
  const color = car.Color || car.farve?.da || car.farve || 'Ukendt'
  const firstReg =
    car.RegistrationDate || car.foerste_registreringsdato || (year ? String(year) : '')

  const equipmentList =
    car.ExtraEquipmentList && Array.isArray(car.ExtraEquipmentList) && car.ExtraEquipmentList.length > 0
      ? car.ExtraEquipmentList.map((item) => item.Name).filter(Boolean)
      : (car.udstyr_liste?.da || car.udstyr_liste || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)

  const scrollToContact = (purpose) => {
    if (purpose) {
      setFormPurpose(purpose)
    }
    const el = document.getElementById('kontakt-sektion')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Webform indsendelse
  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormLoading(true)
    setFormError('')

    const form = e.target
    const formData = new FormData(form)

    const userMessage = formData.get('besked') || ''
    const priceText = hasBoth 
      ? `Kontant: ${cashPrice.toLocaleString('da-DK')} kr. | Leasing: ${leasingPrice.toLocaleString('da-DK')} kr./md.`
      : leasingPrice > 0
      ? `Leasing: ${leasingPrice.toLocaleString('da-DK')} kr./md.`
      : cashPrice > 0
      ? `Kontant: ${cashPrice.toLocaleString('da-DK')} kr.`
      : 'Pris ikke oplyst'

    const carDetailsHeader = `Vedrørende: ${title} (${priceText})
Link til bil: https://recars.dk/biler-til-salg/${slug}
Formål: ${formData.get('formaal') || 'Generelt'}

--- Kundens besked ---
`
    const combinedBesked = userMessage.includes(title) 
      ? userMessage 
      : `${carDetailsHeader}${userMessage}`

    const payload = {
      website_url: formData.get('website_url') || '',
      navn: formData.get('navn'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      bil: `${title} (${priceText})`,
      bil_link: `https://recars.dk/biler-til-salg/${slug}`,
      formaal: formData.get('formaal'),
      besked: combinedBesked,
      bil_titel: title,
      bil_slug: slug,
      bil_pris: priceText,
      emne: `Henvendelse vedr. ${title}`,
      type: 'bilsalg_henvendelse'
    }

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (res.ok) {
        setFormSubmitted(true)
      } else {
        setFormError('Der opstod en fejl ved afsendelse af formularen. Prøv venligst igen.')
      }
    } catch (err) {
      console.error(err)
      setFormError('Netværksfejl. Kontroller din internetforbindelse og prøv igen.')
    } finally {
      setFormLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{title} | Recars ApS</title>
        <meta
          name="description"
          content={`Se specifikationer, billeder og udstyr på denne ${title} hos Recars ApS i Børkop.`}
        />
        <meta property="og:title" content={`${title} | Recars ApS`} />
        <meta
          property="og:description"
          content={`Se specifikationer, billeder og udstyr på denne ${title} hos Recars ApS i Børkop.`}
        />
        {images[0] && <meta property="og:image" content={images[0]} />}
        <link rel="canonical" href={`https://recars.dk/biler-til-salg/${slug}`} />
      </Helmet>

      {/* Hovedside Container */}
      <div className="min-h-screen bg-[#FAF9F6] pt-32 sm:pt-40 lg:pt-44 pb-24 text-neutral-800">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12">
          {/* ============================================================ */}
          {/* TOP BAR / BREADCRUMB: Tilbage til oversigt */}
          {/* ============================================================ */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-stone-200/80 pb-4">
            <Link
              to="/biler-til-salg"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-600 hover:text-neutral-950 transition-colors group"
            >
              <i className="fa-solid fa-arrow-left text-[11px] group-hover:-translate-x-0.5 transition-transform" />
              <span>Tilbage til oversigt</span>
            </Link>

            <div className="flex items-center gap-2 text-xs font-medium text-neutral-600">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
              <span>Placeret hos Recars i Børkop</span>
            </div>
          </div>

          {/* ============================================================ */}
          {/* SEKTION 1: VEHICLE DETAIL & CONVERSION HERO (2-KOLONNE GRID) */}
          {/* ============================================================ */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
            {/* VENSTRE KOLONNE (lg:col-span-7): Media Galleri */}
            <div className="lg:col-span-7 flex flex-col gap-3.5">
              {/* Stort Hovedbillede (16:10 format) */}
              <div className="relative group overflow-hidden rounded-2xl border border-stone-200 bg-white aspect-[16/10] shadow-sm">
                {images.length > 0 ? (
                  <>
                    <img
                      src={getResizedImageUrl(images[selectedImageIndex] || images[0], 1200)}
                      alt={`${title} - Billede ${selectedImageIndex + 1}`}
                      className="h-full w-full object-cover cursor-zoom-in transition-transform duration-500 group-hover:scale-[1.015]"
                      onClick={() => setActiveImageIndex(selectedImageIndex)}
                    />

                    {/* Billedetæller badge */}
                    <div className="absolute top-4 left-4 z-10 flex items-center gap-2 rounded-full bg-black/75 backdrop-blur-md px-3.5 py-1.5 text-xs font-medium text-white border border-white/10">
                      <i className="fa-regular fa-image text-[#DFB587]" />
                      <span>
                        {selectedImageIndex + 1} / {images.length}
                      </span>
                    </div>

                    {/* Fuldskærm Zoom-knap */}
                    <button
                      type="button"
                      onClick={() => setActiveImageIndex(selectedImageIndex)}
                      className="absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/75 backdrop-blur-md text-white border border-white/10 hover:bg-[#DFB587] hover:text-black transition-all shadow-md"
                      title="Åbn i fuld skærm"
                      aria-label="Åbn billede i fuld skærm"
                    >
                      <i className="fa-solid fa-expand text-xs" />
                    </button>

                    {/* Forrige / Næste overlay pile */}
                    {images.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedImageIndex((prev) =>
                              prev === 0 ? images.length - 1 : prev - 1
                            )
                          }}
                          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-neutral-900 hover:text-white transition-all duration-200 shadow-md"
                          title="Forrige billede"
                          aria-label="Forrige billede"
                        >
                          <i className="fa-solid fa-chevron-left text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedImageIndex((prev) =>
                              prev === images.length - 1 ? 0 : prev + 1
                            )
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-neutral-900 hover:text-white transition-all duration-200 shadow-md"
                          title="Næste billede"
                          aria-label="Næste billede"
                        >
                          <i className="fa-solid fa-chevron-right text-sm" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-neutral-400">
                    Ingen billeder tilgængelige
                  </div>
                )}
              </div>

              {/* Thumbnail Strip (Række under billedet) */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2.5">
                  {images.slice(0, 5).map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedImageIndex(idx)}
                      className={`relative overflow-hidden rounded-xl border aspect-[16/10] transition-all duration-200 bg-white ${
                        selectedImageIndex === idx
                          ? 'border-[#9b784a] ring-2 ring-[#DFB587]/40 scale-[1.02]'
                          : 'border-stone-200 opacity-80 hover:opacity-100 hover:border-stone-400'
                      }`}
                    >
                      <img
                        src={getResizedImageUrl(img, 240)}
                        alt={`${title} thumbnail ${idx + 1}`}
                        className="h-full w-full object-cover"
                      />
                      {images.length > 5 && idx === 4 && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation()
                            setActiveImageIndex(4)
                          }}
                          className="absolute inset-0 bg-black/75 flex items-center justify-center text-[11px] font-bold text-white uppercase tracking-wider hover:bg-black/85"
                        >
                          +{images.length - 5} fotos
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* HØJRE KOLONNE (lg:col-span-5): Specs, Pris & Handling */}
            <div className="lg:col-span-5 flex flex-col justify-between rounded-2xl border border-stone-200 bg-white p-6 sm:p-8 shadow-sm">
              <div>
                {/* Badges & Status */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {isSold ? (
                    <span className="rounded-md bg-red-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                      Solgt
                    </span>
                  ) : (
                    <span className="rounded-md bg-neutral-900 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                      Til salg
                    </span>
                  )}
                  {hasBoth && !isSold && (
                    <span className="rounded-md bg-[#DFB587]/20 border border-[#DFB587]/60 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[#9b784a]">
                      Køb & Leasing
                    </span>
                  )}
                  {!hasBoth && leasingPrice > 0 && !isSold && (
                    <span className="rounded-md bg-stone-900 border border-stone-800 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                      Leasing
                    </span>
                  )}
                  <span className="rounded-md bg-stone-100 border border-stone-200/80 px-2.5 py-1 text-[11px] font-semibold text-neutral-700">
                    Modelår {year}
                  </span>
                  <span className="rounded-md bg-stone-100 border border-stone-200/80 px-2.5 py-1 text-[11px] font-semibold text-neutral-700">
                    {fuel}
                  </span>
                </div>

                {/* Bilens Titel */}
                <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-neutral-950 leading-snug">
                  {title}
                </h1>

                {/* Nøglespecifikationer (Struktureret som Vestjysk Bilhus) */}
                <div className="mt-6 border-t border-stone-200 py-4 grid grid-cols-2 gap-y-3.5 gap-x-6 text-xs">
                  <div>
                    <span className="text-neutral-500 block text-[11px]">Kilometer</span>
                    <span className="font-semibold text-neutral-900 text-sm">
                      {mileage?.toLocaleString('da-DK')} km
                    </span>
                  </div>

                  <div>
                    <span className="text-neutral-500 block text-[11px]">Modelår</span>
                    <span className="font-semibold text-neutral-900 text-sm">{year}</span>
                  </div>

                  <div>
                    <span className="text-neutral-500 block text-[11px]">Drivmiddel</span>
                    <span className="font-semibold text-neutral-900 text-sm truncate block" title={fuel}>
                      {fuel}
                    </span>
                  </div>

                  {range ? (
                    <div>
                      <span className="text-emerald-700 block text-[11px] font-medium">Rækkevidde (WLTP)</span>
                      <span className="font-semibold text-emerald-900 text-sm">{range} km</span>
                    </div>
                  ) : consumption ? (
                    <div>
                      <span className="text-neutral-500 block text-[11px]">Forbrug</span>
                      <span className="font-semibold text-neutral-900 text-sm truncate block" title={consumption}>
                        {consumption}
                      </span>
                    </div>
                  ) : null}

                  <div>
                    <span className="text-neutral-500 block text-[11px]">Gearkasse</span>
                    <span className="font-semibold text-neutral-900 text-sm truncate block" title={transmission}>
                      {transmission}
                    </span>
                  </div>

                  {horsepower ? (
                    <div>
                      <span className="text-neutral-500 block text-[11px]">Hestekræfter</span>
                      <span className="font-semibold text-neutral-900 text-sm">{horsepower} hk</span>
                    </div>
                  ) : firstReg ? (
                    <div>
                      <span className="text-neutral-500 block text-[11px]">1. Registrering</span>
                      <span className="font-semibold text-neutral-900 text-sm">{firstReg}</span>
                    </div>
                  ) : null}
                </div>

                {/* Kontantpris / Leasingpris */}
                <div className="border-t border-stone-200 pt-5 pb-6">
                  {hasBoth ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-stone-200 bg-stone-50/70 p-3.5">
                          <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold block mb-1">
                            Kontantpris
                          </span>
                          <p className="font-display text-2xl font-extrabold text-neutral-950 tracking-tight">
                            {cashPrice.toLocaleString('da-DK')} kr.
                          </p>
                        </div>

                        <div className="rounded-xl border border-[#DFB587]/60 bg-[#DFB587]/10 p-3.5">
                          <span className="text-[10px] uppercase tracking-wider text-[#9b784a] font-bold block mb-1">
                            Mdl. leasingydelse
                          </span>
                          <p className="font-display text-2xl font-extrabold text-neutral-950 tracking-tight">
                            {leasingPrice.toLocaleString('da-DK')} kr.<span className="text-xs font-semibold text-neutral-600">/md.</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : leasingPrice > 0 ? (
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold block mb-1">
                        Leasingpris (mdl. ydelse)
                      </span>
                      <p className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-950 tracking-tight">
                        {leasingPrice.toLocaleString('da-DK')} kr.<span className="text-base font-semibold text-neutral-500">/md.</span>
                      </p>
                    </div>
                  ) : (
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold block mb-1">
                        Kontantpris
                      </span>
                      <p className="font-display text-3xl sm:text-4xl font-extrabold text-neutral-950 tracking-tight">
                        {cashPrice ? `${cashPrice.toLocaleString('da-DK')} kr.` : 'Kontakt for pris'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Handlingsknapper (CTAs) */}
              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => scrollToContact('proevetur')}
                  className="w-full rounded-xl bg-neutral-950 py-3.5 px-6 text-center text-xs font-bold uppercase tracking-wider text-white hover:bg-[#9b784a] transition-all duration-200 shadow-sm flex items-center justify-center gap-2 group"
                >
                  <i className="fa-solid fa-calendar-check text-[#DFB587]" />
                  <span>Bestil prøvetur</span>
                </button>

                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="tel:+4571999177"
                    className="rounded-xl border border-stone-300 bg-white py-3 px-4 text-center text-xs font-bold uppercase tracking-wider text-neutral-900 hover:border-neutral-900 transition-all flex items-center justify-center gap-2"
                  >
                    <i className="fa-solid fa-phone text-[#9b784a]" />
                    <span>Ring til os</span>
                  </a>

                  <button
                    type="button"
                    onClick={() => scrollToContact('spoergsmaal')}
                    className="rounded-xl border border-stone-300 bg-white py-3 px-4 text-center text-xs font-bold uppercase tracking-wider text-neutral-900 hover:border-neutral-900 transition-all flex items-center justify-center gap-2"
                  >
                    <i className="fa-solid fa-envelope text-[#9b784a]" />
                    <span>Stil spørgsmål</span>
                  </button>
                </div>

                <div className="mt-4 pt-4 border-t border-stone-100 text-xs text-neutral-500 flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#9b784a]" />
                  <span>Besigtigelse & prøvekørsel i Børkop efter aftale</span>
                </div>
              </div>
            </div>
          </div>

          {/* ============================================================ */}
          {/* SEKTION 2: BESKRIVELSE (Ligesom Vestjysk Bilhus)              */}
          {/* ============================================================ */}
          <div className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-10 shadow-sm mb-12 text-left">
            <div className="flex items-center gap-3 border-b border-stone-100 pb-5 mb-6">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-stone-100 text-[#9b784a]">
                <i className="fa-solid fa-align-left" />
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-neutral-950">
                Beskrivelse
              </h2>
            </div>

            <div
              className="prose prose-stone max-w-none text-neutral-700 leading-relaxed font-sans space-y-4 text-sm sm:text-base"
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          </div>

          {/* ============================================================ */}
          {/* SEKTION 3: SPECIFIKATIONER (Kategoriseret som Vestjysk Bilhus) */}
          {/* ============================================================ */}
          <div className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-10 shadow-sm mb-12 text-left">
            <div className="flex items-center gap-3 border-b border-stone-100 pb-5 mb-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-stone-100 text-[#9b784a]">
                <i className="fa-solid fa-sliders" />
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-neutral-950">
                Specifikationer
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
              {/* Kategori 1: Model & Historik */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#9b784a] mb-4 pb-2 border-b border-stone-100 flex items-center gap-2">
                  <i className="fa-solid fa-car-side" />
                  <span>Model</span>
                </h3>

                <div className="divide-y divide-stone-100 text-xs sm:text-sm">
                  <div className="py-3 flex justify-between items-center">
                    <span className="text-neutral-500">Mærke og model</span>
                    <span className="font-semibold text-neutral-900 text-right">{title}</span>
                  </div>

                  <div className="py-3 flex justify-between items-center">
                    <span className="text-neutral-500">Modelår</span>
                    <span className="font-semibold text-neutral-900">{year}</span>
                  </div>

                  <div className="py-3 flex justify-between items-center">
                    <span className="text-neutral-500">1. Registrering</span>
                    <span className="font-semibold text-neutral-900">{firstReg || year}</span>
                  </div>

                  <div className="py-3 flex justify-between items-center">
                    <span className="text-neutral-500">Kilometer</span>
                    <span className="font-semibold text-neutral-900">
                      {mileage?.toLocaleString('da-DK')} km
                    </span>
                  </div>

                  {color && (
                    <div className="py-3 flex justify-between items-center">
                      <span className="text-neutral-500">Farve</span>
                      <span className="font-semibold text-neutral-900">{color}</span>
                    </div>
                  )}

                  <div className="py-3 flex justify-between items-center">
                    <span className="text-neutral-500">Levering</span>
                    <span className="font-semibold text-emerald-700">Klar til levering i Børkop</span>
                  </div>
                </div>
              </div>

              {/* Kategori 2: Motor og miljø */}
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#9b784a] mb-4 pb-2 border-b border-stone-100 flex items-center gap-2">
                  <i className="fa-solid fa-leaf" />
                  <span>Motor og miljø</span>
                </h3>

                <div className="divide-y divide-stone-100 text-xs sm:text-sm">
                  <div className="py-3 flex justify-between items-center">
                    <span className="text-neutral-500">Drivmiddel</span>
                    <span className="font-semibold text-neutral-900">{fuel}</span>
                  </div>

                  <div className="py-3 flex justify-between items-center">
                    <span className="text-neutral-500">Gearkasse</span>
                    <span className="font-semibold text-neutral-900">{transmission}</span>
                  </div>

                  {horsepower ? (
                    <div className="py-3 flex justify-between items-center">
                      <span className="text-neutral-500">Hestekræfter</span>
                      <span className="font-semibold text-neutral-900">{horsepower} hk</span>
                    </div>
                  ) : null}

                  {range ? (
                    <div className="py-3 flex justify-between items-center bg-emerald-50/60 px-2.5 -mx-2.5 rounded-lg">
                      <span className="text-emerald-800 font-medium flex items-center gap-2">
                        <i className="fa-solid fa-bolt text-emerald-600" /> Rækkevidde (WLTP)
                      </span>
                      <span className="font-bold text-emerald-900">{range} km</span>
                    </div>
                  ) : null}

                  {battery ? (
                    <div className="py-3 flex justify-between items-center">
                      <span className="text-neutral-500">Batterikapacitet</span>
                      <span className="font-semibold text-neutral-900">{battery} kWh</span>
                    </div>
                  ) : null}

                  {consumption ? (
                    <div className="py-3 flex justify-between items-center">
                      <span className="text-neutral-500">Energiforbrug</span>
                      <span className="font-semibold text-neutral-900">{consumption}</span>
                    </div>
                  ) : null}

                  {greenTax ? (
                    <div className="py-3 flex justify-between items-center">
                      <span className="text-neutral-500">Grøn ejerafgift</span>
                      <span className="font-semibold text-neutral-900">{greenTax}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Kategori 3: Udstyr */}
            {equipmentList.length > 0 && (
              <div className="mt-12 pt-10 border-t border-stone-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-[#9b784a] flex items-center gap-2">
                    <i className="fa-solid fa-list-check" />
                    <span>Udstyr ({equipmentList.length} udstyrspunkter)</span>
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {equipmentList.map((item, idx) => (
                    <div
                      key={idx}
                      className="group flex items-center gap-2.5 rounded-xl border border-stone-200/80 bg-stone-50/60 hover:bg-white hover:border-[#dfb587]/60 hover:shadow-xs px-3.5 py-2.5 text-xs text-neutral-800 transition-all duration-150"
                    >
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#dfb587]/15 text-[#9b784a] ring-1 ring-[#dfb587]/30 group-hover:bg-[#dfb587] group-hover:text-white transition-colors duration-150">
                        <i className="fa-solid fa-check text-[10px]" aria-hidden="true" />
                      </span>
                      <span className="font-medium text-neutral-800 leading-snug">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ============================================================ */}
          {/* SEKTION 4: KONTAKT & PRØVEKØRSEL (Henvendelse for denne bil) */}
          {/* ============================================================ */}
          <div id="kontakt-sektion" className="scroll-mt-32 sm:scroll-mt-36 rounded-2xl border border-stone-200 bg-white p-6 sm:p-10 shadow-sm mb-16 text-left">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              {/* Venstre side: Information & Åbningstider */}
              <div className="lg:col-span-5 flex flex-col justify-between h-full">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#9b784a] block mb-1">
                    Kontakt & Prøvekørsel
                  </span>
                  <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-neutral-950 mb-3">
                    Interesseret i denne {title}?
                  </h2>
                  <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed mb-6">
                    Kontakt os i dag for at aftale en prøvekørsel på pladsen i Børkop eller for at få en vurdering af din eventuelle byttebil.
                  </p>

                  <div className="space-y-4 border-t border-stone-100 pt-6 text-xs text-neutral-700">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-[#9b784a]">
                        <i className="fa-solid fa-location-dot text-sm" />
                      </div>
                      <div>
                        <p className="font-bold text-neutral-900">Adresse</p>
                        <p className="text-neutral-500">Industrivej 52, 7080 Børkop</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-[#9b784a]">
                        <i className="fa-solid fa-phone text-sm" />
                      </div>
                      <div>
                        <p className="font-bold text-neutral-900">Telefon</p>
                        <a href="tel:+4571999177" className="text-neutral-500 hover:text-neutral-950 transition-colors">
                          +45 71 99 91 77
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-[#9b784a]">
                        <i className="fa-solid fa-envelope text-sm" />
                      </div>
                      <div>
                        <p className="font-bold text-neutral-900">E-mail</p>
                        <a href="mailto:info@recars.dk" className="text-neutral-500 hover:text-neutral-950 transition-colors">
                          info@recars.dk
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Højre side: Webform Formular */}
              <div className="lg:col-span-7 bg-stone-50/60 rounded-xl border border-stone-200/70 p-6 sm:p-8">
                {formSubmitted ? (
                  <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-8 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 mx-auto mb-4">
                      <i className="fa-solid fa-check text-lg" />
                    </div>
                    <h3 className="font-display text-base font-bold text-emerald-900 uppercase tracking-wider">
                      Tak for din henvendelse!
                    </h3>
                    <p className="text-xs sm:text-sm text-emerald-700 mt-2">
                      Vi har modtaget din besked om {title} og vender hurtigt tilbage.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    {/* Antispam Honeypot - ABSOLUTELY HIDDEN */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Do not fill this out</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Navn */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                          Dit Navn *
                        </label>
                        <input
                          type="text"
                          name="navn"
                          required
                          placeholder="F.eks. Anders Jensen"
                          className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-neutral-900 focus:outline-none transition-colors"
                        />
                      </div>

                      {/* Telefon */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                          Telefonnummer *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="Dit telefonnummer"
                          className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-neutral-900 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Email */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                          E-mailadresse *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="din@email.dk"
                          className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-neutral-900 focus:outline-none transition-colors"
                        />
                      </div>

                      {/* Formål */}
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                          Hvad drejer henvendelsen sig om?
                        </label>
                        <select
                          name="formaal"
                          value={formPurpose}
                          onChange={(e) => setFormPurpose(e.target.value)}
                          className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-neutral-900 focus:border-neutral-900 focus:outline-none transition-colors"
                        >
                          <option value="proevetur">Ønsker prøvekørsel i Børkop</option>
                          <option value="byttebil">Byttepris på min nuværende bil</option>
                          <option value="spoergsmaal">Generelle spørgsmål om bilen</option>
                          {cashPrice > 0 && <option value="koeb">Køb af bilen</option>}
                          {leasingPrice > 0 && <option value="leasing">Leasingtilbud på bilen</option>}
                        </select>
                      </div>
                    </div>

                    {/* Besked */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                        Besked
                      </label>
                      <textarea
                        name="besked"
                        rows="3"
                        defaultValue={`Hej Recars, jeg er interesseret i denne ${title} (${
                          price ? `${price.toLocaleString('da-DK')} kr.` : ''
                        }) og vil gerne høre mere.`}
                        className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-neutral-900 placeholder-neutral-400 focus:border-neutral-900 focus:outline-none transition-colors resize-none"
                      />
                    </div>

                    {formError && (
                      <p className="text-xs text-red-600 font-medium">{formError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={formLoading}
                      className="mt-2 w-full rounded-xl bg-neutral-950 py-3.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#9b784a] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {formLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="fa-solid fa-spinner animate-spin" /> Sender henvendelse...
                        </span>
                      ) : (
                        'Send henvendelse'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* ============================================================ */}
          {/* SEKTION 6: RELATEREDE BILER PÅ LAGER                         */}
          {/* ============================================================ */}
          {relatedCars.length > 0 && (
            <div className="mt-12 border-t border-stone-200/80 pt-14 text-left">
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#9b784a]">
                    Udvalgte modeller
                  </span>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-neutral-950 mt-1">
                    Flere biler på lager
                  </h2>
                </div>
                <Link
                  to="/biler-til-salg"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-600 hover:text-neutral-950 transition-colors"
                >
                  <span>Se alle biler</span>
                  <i className="fa-solid fa-arrow-right text-[11px]" />
                </Link>
              </div>

              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {relatedCars.map((c) => (
                  <CarCard key={c.Id || c.VehicleId || c.id || c.slug} car={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Flydende / Sticky "Bestil prøvetur" bjælke der følger med på siden */}
      <div
        className={`fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-40 transition-all duration-300 ease-out pointer-events-none ${
          showStickyBar
            ? 'translate-y-0 opacity-100'
            : 'translate-y-8 opacity-0'
        }`}
      >
        <div className="pointer-events-auto bg-neutral-950/95 text-white backdrop-blur-md rounded-2xl border border-neutral-800/90 shadow-[0_10px_35px_rgba(0,0,0,0.45)] p-2 sm:p-2.5 flex items-center gap-2 sm:gap-3">
          {/* Kort bilinfo / pris */}
          <div className="flex items-center gap-2 pl-1 sm:pl-2">
            {images && images.length > 0 && (
              <img
                src={images[0]}
                alt={title}
                className="h-8 w-10 sm:h-9 sm:w-11 rounded-lg object-cover bg-neutral-900 border border-neutral-800 shrink-0 hidden xs:block"
              />
            )}
            <div className="flex flex-col justify-center min-w-0 pr-1 sm:pr-2">
              <span className="text-[11px] font-medium text-neutral-400 max-w-[120px] sm:max-w-[180px] truncate leading-tight">
                {title}
              </span>
              {hasBoth ? (
                <div className="flex items-center gap-1.5 text-xs sm:text-sm font-display font-bold leading-tight">
                  <span className="text-white">{cashPrice.toLocaleString('da-DK')} kr.</span>
                  <span className="text-neutral-500 text-[10px]">·</span>
                  <span className="text-[#DFB587] text-[11px] sm:text-xs">{leasingPrice.toLocaleString('da-DK')} kr./md.</span>
                </div>
              ) : leasingPrice > 0 ? (
                <span className="font-display font-bold text-xs sm:text-sm text-[#DFB587] tracking-tight leading-tight">
                  {leasingPrice.toLocaleString('da-DK')} kr./md.
                </span>
              ) : cashPrice > 0 ? (
                <span className="font-display font-bold text-xs sm:text-sm text-[#DFB587] tracking-tight leading-tight">
                  {cashPrice.toLocaleString('da-DK')} kr.
                </span>
              ) : null}
            </div>
          </div>

          {/* Separator */}
          <div className="h-7 w-px bg-neutral-800 shrink-0 hidden xs:block" />

          {/* Handlinger */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <a
              href="tel:+4571999177"
              className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl border border-neutral-800 bg-neutral-900/90 text-[#DFB587] hover:bg-neutral-800 hover:border-[#DFB587]/60 transition-colors shrink-0"
              aria-label="Ring til Recars (+45 71 99 91 77)"
              title="Ring: 71 99 91 77"
            >
              <i className="fa-solid fa-phone text-xs" />
            </a>

            <button
              type="button"
              onClick={() => scrollToContact('proevetur')}
              className="rounded-xl bg-[#DFB587] hover:bg-[#cf9e68] text-neutral-950 font-bold text-[11px] sm:text-xs uppercase tracking-wider px-3 sm:px-3.5 py-2 shadow transition-all duration-200 flex items-center gap-1.5 shrink-0 hover:scale-[1.02] active:scale-95 group cursor-pointer"
            >
              <i className="fa-solid fa-calendar-check text-neutral-950 text-xs group-hover:scale-110 transition-transform" />
              <span>Bestil prøvetur</span>
            </button>
          </div>
        </div>
      </div>

      {/* Google Maps Sektion */}
      <GoogleMap />

      {/* Fuldskærm Lightbox Modal */}
      {activeImageIndex >= 0 && images && images.length > 0 && (
        <GalleryModal
          images={images}
          currentIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(-1)}
          onPrev={() =>
            setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
          }
          onNext={() =>
            setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
          }
        />
      )}
    </>
  )
}
